import React from 'react';
import classNames from 'classnames';
import TagManager from 'react-gtm-module';
import Bugsnag from '@bugsnag/js';

import './fatzebra.css';
import Loader from '../../../../components/Loader';
import appConfig from '../../../../appConfig';
import { creditcardCreateConfirm } from '../../../../api/deposit';
import paymentStatus from '../../../constants/paymentStatus';
import { parseApiError } from '../../../../utils/parseApiError';

// Successful cards
// Visa:4000000000001000
// Mastercard: 5200000000001005
// Amex: 340000000001007
// OTP Mastercard: 5200000000001096
// Card with response based on last 2 digits of payment amount: 4242424242424242 00 => successful, more here: https://docs.fatzebra.com/docs/response-codes
// Unsuccessful cards:
// Visa: 4000000000002446
// More at https://docs.fatzebra.com/docs/testing

interface Props {
	fatzebra: {
		cardToken?: string;
		payment: {
			amount: number;
			currency: string;
			reference: string;
		};
		token: string;
		verification: string;
	};
	threeDSCheckCallback: (fzExtra: unknown) => Promise<CreditcardDepositResponse>;
	user: PortalUser;
	showSuccessfulResultMessage: (show: boolean) => void;
	showUnsuccessfulResultMessage: (show: boolean) => void;
	setErrorMessage: (error: string | undefined) => void;
	promote?: {
		title?: string;
		content?: string;
		clientId?: number;
	};
}

interface IframeMessage extends MessageEvent {
	data:
		| {
				type: 'log';
				message: string;
		  }
		| {
				type: 'error';
				message: string;
		  }
		| {
				type: 'loaded';
		  }
		| {
				type: 'submitting';
		  }
		| {
				type: 'sdkEvent';
				message: {
					fzDetails: {
						data: any;
						errors: Array<string>;
						message: string;
					};
					type: 'validationError' | 'paymentError' | 'success';
					context: string;
					message: string;
				};
		  };
}

const CardIframe: React.FC<Props> = ({
	fatzebra,
	threeDSCheckCallback,
	user,
	showSuccessfulResultMessage,
	showUnsuccessfulResultMessage,
	setErrorMessage,
	promote,
}) => {
	const fatZebraIframeRef = React.useRef<HTMLIFrameElement>(null);
	const threeDscheckResultRef = React.useRef<unknown>();
	// using double negation because I want this variable to be a boolean (not string | undefined)
	const is3DSCheck = !!fatzebra?.cardToken;

	const [fatzebraState, setFatzebraState] = React.useState<{
		loaded: boolean;
		processing: boolean;
		error?: string | null;
		formValidationErrors: Array<string>;
		paymentSuccess: boolean;
	}>({
		loaded: false,
		processing: false,
		error: null,
		formValidationErrors: [],
		paymentSuccess: false,
	});

	const postMessage = (message: any) => {
		fatZebraIframeRef.current?.contentWindow?.postMessage(message, window.location.origin);
	};

	React.useEffect(() => {
		if (fatzebraState.paymentSuccess) {
			showSuccessfulResultMessage(true);
		}
		if (!fatzebraState.processing && fatzebraState.error) {
			showUnsuccessfulResultMessage(true);
			setErrorMessage(fatzebraState.error);
		}
	}, [
		fatzebraState.paymentSuccess,
		fatzebraState.error,
		fatzebraState.processing,
		showSuccessfulResultMessage,
		showUnsuccessfulResultMessage,
		setErrorMessage,
	]);

	React.useEffect(() => {
		const currentIframe = fatZebraIframeRef.current;

		const messageListener = ({ data }: IframeMessage) => {
			// when debugging credit card payments this is the first thing you want to uncomment:
			// console.log('iframe message', data);

			switch (data.type) {
				case 'loaded':
					setFatzebraState((oldState) => ({
						...oldState,
						loaded: true,
					}));
					break;

				case 'submitting':
					setFatzebraState((oldState) => ({
						...oldState,
						processing: true,
					}));
					break;

				case 'error':
					setFatzebraState((oldState) => ({
						...oldState,
						error: data.message,
					}));
					break;

				case 'sdkEvent':
					if (data.message.type === 'paymentError') {
						setFatzebraState((oldState) => ({
							...oldState,
							error: data.message.fzDetails.message ?? data.message.message,
							processing: false,
						}));

						Bugsnag.notify(JSON.stringify(data.message.fzDetails));
					} else if (data.message.type === 'validationError')
						setFatzebraState((oldState) => ({
							...oldState,
							processing: false,
						}));
					else if (data.message.type === 'success') {
						if (data.message.context === 'payment') {
							TagManager.dataLayer({
								dataLayer: {
									event: 'deposit',
									deposit_amount: fatzebra?.payment?.amount / 100,
									deposit_method: 'Debit Card',
								},
							});

							setFatzebraState((oldState) => ({
								...oldState,
								processing: false,
								paymentSuccess: true,
							}));

							creditcardCreateConfirm(data.message?.fzDetails?.data?.transactionId, threeDscheckResultRef.current);
						}

						if (data.message.context === '3DS2') {
							threeDscheckResultRef.current = data.message?.fzDetails?.data;

							if (is3DSCheck) {
								threeDSCheckCallback(threeDscheckResultRef.current)
									.then((result) => {
										if (result.data.status.id === paymentStatus.Approved) {
											if (appConfig.googleTagManagerID) {
												TagManager.dataLayer({
													dataLayer: {
														event: 'deposit',
														deposit_amount: fatzebra?.payment?.amount / 100,
														deposit_method: 'Debit Card',
													},
												});

												setFatzebraState((oldState) => ({
													...oldState,
													processing: false,
													paymentSuccess: true,
												}));
											}
										} else
											setFatzebraState((oldState) => ({
												...oldState,
												processing: false,
												paymentSuccess: false,
												error: '',
											}));
									})
									.catch((error) => {
										setFatzebraState((oldState) => ({
											...oldState,
											processing: false,
											paymentSuccess: false,
											error: parseApiError(error.errors),
										}));
									});
							}
						}
					}
					break;

				default:
					break;
			}
		};

		window.addEventListener('message', messageListener);

		const loadListener = () => {
			if (promote)
				postMessage({
					type: 'setPromotion',
					params: promote,
				});

			postMessage({
				type: 'fatZebraInit',
				params: {
					...fatzebra,
					customer: {
						address: `${user?.street_number ?? ''} ${user?.street_name ?? ''} ${user?.street_type ?? ''}`,
						city: user?.city ?? '',
						country: user?.country ?? '',
						email: user?.email ?? '',
						firstName: user?.first_name ?? '',
						lastName: user?.last_name ?? '',
						postcode: user?.postcode ?? '',
						state: user?.state ?? '',
					},
					fzUsername: appConfig.fatZebra.userName,
					fzSDK: appConfig.fatZebra.sdk,
				},
			});
		};

		fatZebraIframeRef.current?.addEventListener('load', loadListener);

		return () => {
			currentIframe?.removeEventListener('load', loadListener);
			window.removeEventListener('message', messageListener);

			currentIframe?.contentWindow?.location.reload();
		};
	}, [
		fatzebra,
		is3DSCheck,
		threeDSCheckCallback,
		user?.city,
		user?.country,
		user?.email,
		user?.first_name,
		user?.last_name,
		user?.postcode,
		user?.state,
		user?.street_name,
		user?.street_number,
		user?.street_type,
		promote,
	]);

	return (
		<div className="_relative">
			{!fatzebraState.loaded && (
				<div style={{ display: 'flex', justifyContent: 'center' }}>
					<Loader />
				</div>
			)}
			<iframe
				ref={fatZebraIframeRef}
				title="FatZebra"
				id="fzIframe"
				className={classNames({
					'fz-loaded': fatzebraState.loaded,
				})}
				src="/fatZebra.html?v3"
			/>
			{fatzebraState.loaded && !fatzebraState.paymentSuccess && !fatzebraState.error && is3DSCheck && (
				<div style={{ display: 'flex', justifyContent: 'center' }}>
					<Loader />
				</div>
			)}
		</div>
	);
};

export default CardIframe;
